// CompleteProfileModal — soft-gate for the 2026-05-18 identity rollout.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS EXISTS (Phase D5)
// ════════════════════════════════════════════════════════════════════
//
// Migrations 088 + 089 added first_name + last_name to admins and
// agents (plus birthdate on agents), but left them NULLable so the
// migration didn't break existing rows. New agent creation now
// MANDATES all three at the API layer (Phase D3 + D4). Existing
// agents who logged in before this deploy still have NULL values.
//
// Until they fill their profile, peer views render a dash ("—") in
// place of their display name — every place their downline / upline
// would have seen "@their_username" now shows that dash. The locked
// product policy is: never leak the username, ever; show a dash
// until the agent self-completes.
//
// This modal nudges them. It opens automatically on session boot
// when first_name OR last_name (OR birthdate, for role='agent') is
// missing. It is DISMISSABLE — agents can click "Later" and continue
// using the app — but it re-opens on the next page load. There is
// no hard gate; that was your explicit call ("no hard gate"). The
// dismiss state is held in component memory only, so any reload /
// re-login re-triggers it until first/last/birthdate are saved.
//
// The modal POSTs to the same /admin/auth/me endpoint the agent
// profile form uses, with the same validation rules (min 2 chars,
// YYYY-MM-DD).

import { FormEvent, useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type Tone = 'ok' | 'err';

export default function CompleteProfileModal() {
  const { admin, refresh } = useAdminAuth();
  const [dismissed, setDismissed] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ tone: Tone; msg: string } | null>(null);

  useEffect(() => {
    if (admin) {
      setFirstName(admin.first_name ?? '');
      setLastName(admin.last_name  ?? '');
      setBirthdate(admin.birthdate ?? '');
    }
  }, [admin?.id, admin?.first_name, admin?.last_name, admin?.birthdate]);

  // Hidden when there's no session, or when first/last (+ birthdate for
  // agents) are already populated, or when the user has dismissed for
  // this page-life. Re-opens on next mount otherwise.
  if (!admin) return null;
  if (dismissed) return null;
  const isAgent = admin.role === 'agent';
  const needsName = !((admin.first_name || '').trim() && (admin.last_name || '').trim());
  const needsBirthdate = isAgent && !((admin.birthdate || '').trim());
  if (!needsName && !needsBirthdate) return null;

  const namesValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length  >= 2 &&
    (!isAgent || /^\d{4}-\d{2}-\d{2}$/.test(birthdate));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!namesValid || busy) return;
    setBusy(true); setToast(null);
    try {
      const body: Record<string, string> = {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
      };
      if (isAgent && birthdate) body.birthdate = birthdate;
      await adminApi.put('/admin/auth/me', body);
      await refresh();
      setToast({ tone: 'ok', msg: 'Profile saved' });
      // Brief success flash, then dismiss on the next tick.
      setTimeout(() => setDismissed(true), 600);
    } catch (err) {
      setToast({ tone: 'err', msg: (err as ApiError).message || 'Save failed' });
    } finally {
      setBusy(false);
    }
  }

  function onLater() {
    // Soft dismiss: hidden for this session, will return on next mount.
    setDismissed(true);
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm grid place-items-start sm:place-items-center px-3 sm:px-4 py-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-5 space-y-4 shadow-2xl my-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-violet-50 text-violet-700 ring-1 ring-violet-100
                           dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30 shrink-0">
            <Icon name="badge" size={20} filled />
          </span>
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-neutral-900 dark:text-white">
              Complete your profile
            </h2>
            <p className="text-[12px] text-arena-700 dark:text-neon-200/75 mt-0.5 leading-relaxed">
              {isAgent
                ? 'Your downline and upline now see "First name Last name" instead of your @username. Until you fill this in, peers see a placeholder — please add it now so you\'re shown by name across the network.'
                : 'Agents now see your name instead of your @username. Add a display name so your downline knows who they\'re talking to.'}
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">First name *</div>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     maxLength={80} placeholder="Rajesh"
                     className="w-full rounded-xl px-3 py-2 text-[14px]
                                bg-white dark:bg-arena-700/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                text-neutral-900 dark:text-white
                                outline-none focus:ring-2 focus:ring-violet-500/30" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">Last name *</div>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                     maxLength={80} placeholder="Sharma"
                     className="w-full rounded-xl px-3 py-2 text-[14px]
                                bg-white dark:bg-arena-700/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                text-neutral-900 dark:text-white
                                outline-none focus:ring-2 focus:ring-violet-500/30" />
            </label>
          </div>
          {isAgent && (
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">Date of birth *</div>
              {/* 2026-05-18 — Cap mobile width; fixed-length date input
                  shouldn't span the full modal row. */}
              <input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)}
                     className="w-full max-w-[10rem] sm:max-w-[12rem] rounded-xl px-3 py-2 text-[14px]
                                bg-white dark:bg-arena-700/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                text-neutral-900 dark:text-white
                                outline-none focus:ring-2 focus:ring-violet-500/30" />
              <span className="block mt-1 text-[10.5px] text-arena-600 dark:text-neon-200/65">Private · never shown to peers</span>
            </label>
          )}

          {toast && (
            <div className={`rounded-xl px-3 py-2 text-[12px] ring-1 ring-inset ${
              toast.tone === 'ok'
                ? 'bg-emerald-50 ring-emerald-200/60 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200'
                : 'bg-rose-50 ring-rose-200/60 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200'
            }`}>{toast.msg}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onLater} disabled={busy}
                    className="px-3 py-1.5 rounded-full text-[12px] font-semibold text-arena-700 dark:text-neon-200/75 hover:bg-arena-100 dark:hover:bg-arena-700/60 disabled:opacity-50">
              Later
            </button>
            <button type="submit" disabled={!namesValid || busy}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold
                               bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                               shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                               disabled:opacity-50 transition active:scale-95">
              <Icon name={busy ? 'progress_activity' : 'save'} size={14} className={busy ? 'animate-spin' : ''} />
              Save profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
